import { useState } from "react";

import ChatbotInterface from "./ChatbotInterface";
import ChatbotWidget from "./ChatbotWidget";
import closeButtonIcon from "../assets/chatbot/close-button.svg";
import loadingSpinner from "../assets/chatbot/loading.svg";
import robotWidgetIcon from "../assets/chatbot/robot-widget.png";

const Chatbot = () => {
  const [chatState, setChatState] = useState("widget"); // 'widget', 'loading', 'open', 'hidden'
  const [messages, setMessages] = useState([]);
  const [isHoveringWidget, setIsHoveringWidget] = useState(false);
  const [sessionId, setSessionId] = useState(null); // NEW: Session ID for chat history
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if this is the first time showing widget

  // Calculate derived state during render - no useEffect needed
  const isWidget = chatState === "widget";
  const isLoading = chatState === "loading";
  const isOpen = chatState === "open";
  const isHidden = chatState === "hidden";

  const handleToggle = () => {
    if (isWidget) {
      setChatState("loading");
      setIsInitialLoad(false); // Mark that we've moved past initial load
      // Simulate brief loading for smooth transition
      setTimeout(() => {
        setChatState("open");
        // Add initial bot message
        setMessages([
          {
            id: 1,
            text: "Hello, I'm Omni, happy to chat!\nDo you want to learn more about our mission?",
            isBot: true,
            timestamp: Date.now(),
          },
        ]);
      }, 1200);
    } else if (isOpen) {
      // Add closing class before changing state
      const chatElement = document.querySelector(".chat-interface-container");
      if (chatElement) {
        chatElement.classList.add("closing");
        setTimeout(() => {
          setChatState("widget");
          setMessages([]);
          setSessionId(null); // Reset session when closing
          setIsEnlarged(false); // Reset enlarged state
        }, 400); // Match animation duration
      } else {
        setChatState("widget");
        setMessages([]);
        setSessionId(null); // Reset session when closing
        setIsEnlarged(false); // Reset enlarged state
      }
    }
  };

  const handleDismissWidget = () => {
    setChatState("hidden");
  };

  const handleSendMessage = async (text) => {
    const newMessage = {
      id: messages.length + 1,
      text,
      isBot: false,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, newMessage]);

    // Create placeholder message for streaming
    const botMessageId = messages.length + 2;
    const placeholderBotMessage = {
      id: botMessageId,
      text: "",
      isBot: true,
      timestamp: Date.now(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, placeholderBotMessage]);

    try {
      const API_BASE_URL =
        window.location.hostname === "localhost"
          ? "http://localhost:3000/api/core"
          : "https://api.openmind.org/api/core";

      // Use fetch with streaming response
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: text,
          ...(sessionId && { session_id: sessionId }), // Include session_id if we have one
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response is JSON (cached) or streaming
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        // Handle cached JSON response
        const data = await response.json();

        // Store session_id if we don't have one yet
        if (data.session_id && !sessionId) {
          setSessionId(data.session_id);
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? {
                  ...msg,
                  text:
                    data.response ||
                    "I'm here to help you learn about OpenMind's mission!",
                  isStreaming: false,
                }
              : msg,
          ),
        );
        return;
      }

      // Read the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = "";

      const updateStreamedMessage = (text) => {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === botMessageId ? { ...msg, text } : msg)),
        );
      };

      const finalizeMessage = (finalText) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? {
                  ...msg,
                  text:
                    finalText ||
                    "I'm here to help you learn about OpenMind's mission!",
                  isStreaming: false,
                }
              : msg,
          ),
        );
      };

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        // Decode the chunk
        const chunk = decoder.decode(value, { stream: true });

        // Parse SSE format (data: {...}\n\n)
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const jsonStr = line.slice(6); // Remove 'data: ' prefix
              if (jsonStr.trim()) {
                const data = JSON.parse(jsonStr);

                // Store session_id if we don't have one yet
                if (data.session_id && !sessionId) {
                  setSessionId(data.session_id);
                }

                if (data.delta) {
                  // Append delta to streamed text
                  streamedText += data.delta;
                  updateStreamedMessage(streamedText);
                } else if (data.final) {
                  // Streaming complete
                  finalizeMessage(data.response || streamedText);
                  return; // Exit the loop
                }
              }
            } catch (parseError) {
              console.error("Error parsing SSE data:", parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error setting up SSE:", error);

      // if SSE setup fails
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessageId
            ? {
                ...msg,
                text: "I'm having trouble connecting right now. Please try again in a moment!",
                isStreaming: false,
              }
            : msg,
        ),
      );
    }
  };

  // Don't render anything if hidden
  if (isHidden) {
    return null;
  }

  return (
    <>
      {/* Widget State - positioned exactly per Figma */}
      {isWidget && (
        <div
          className={`fixed z-50 ${isInitialLoad ? "widget-container-initial" : "widget-container-return"}`}
          style={{
            right: "20px",
            bottom: "20px",
            width: "103px",
            height: "80px",
          }}
          onMouseEnter={() => setIsHoveringWidget(true)}
          onMouseLeave={() => setIsHoveringWidget(false)}
        >
          <ChatbotWidget onToggle={handleToggle} />

          {/* Close button for widget - positioned at top right corner */}
          <div
            className={`absolute close-button-wrapper ${isHoveringWidget ? "show" : ""}`}
            style={{
              right: "-10px",
              top: "-10px",
              width: "28px",
              height: "28px",
            }}
          >
            <button
              onClick={handleDismissWidget}
              className="relative bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow"
              style={{
                width: "28px",
                height: "28px",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <img
                src={closeButtonIcon}
                alt="Close"
                style={{
                  position: "absolute",
                  left: "5px",
                  top: "5px",
                  width: "18.67px",
                  height: "18.67px",
                }}
              />
            </button>
          </div>
        </div>
      )}

      {/* Loading State - Frame 1948760777 (217x105.07px) */}
      {isLoading && (
        <div
          className="fixed z-50"
          style={{
            right: "20px",
            bottom: "20px",
            width: "217px",
            height: "105.07px",
          }}
        >
          <div className="relative w-full h-full">
            {/* Group 2609067 - Loading circle at 89,13 (40x40px) */}
            <div
              className="absolute"
              style={{
                left: "89px",
                top: "13px",
                width: "40px",
                height: "40px",
              }}
            >
              {/* Rectangle 61 - White background circle */}
              <div
                className="absolute bg-white"
                style={{
                  left: "0px",
                  top: "0px",
                  width: "40px",
                  height: "40px",
                  borderRadius: "20px",
                  filter: "drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.25))",
                }}
              />

              {/* Simple loading spinner */}
              <div
                className="absolute"
                style={{
                  left: "8px",
                  top: "8px",
                  width: "24px",
                  height: "24px",
                }}
              >
                <img
                  src={loadingSpinner}
                  alt="Loading..."
                  className="loading-spinner"
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              </div>
            </div>

            {/* UwU 1 - Robot image SEPARATE from loading circle at 114,25 */}
            <div
              className="absolute"
              style={{
                left: "114px",
                top: "25px",
                width: "103px",
                height: "80.07px",
                filter: "drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.25))",
              }}
            >
              <img
                src={robotWidgetIcon}
                alt="Omni Robot"
                className="object-contain w-full h-full"
              />
            </div>
          </div>

          {/* Simple loading animation */}
          <style>{`
            .loading-spinner {
              animation: spin 1s linear infinite;
            }
            
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Chat Interface State - positioned per Figma */}
      {isOpen && (
        <div
          className="fixed z-50 chat-interface-container"
          style={{
            right: "20px",
            bottom: "20px",
            width: isEnlarged ? "400px" : "280px",
            height: isEnlarged ? "600px" : "420px",
            transition: "width 0.3s ease-in-out, height 0.3s ease-in-out",
          }}
        >
          <ChatbotInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            onClose={handleToggle}
            isEnlarged={isEnlarged}
            onToggleEnlarge={() => setIsEnlarged(!isEnlarged)}
          />
        </div>
      )}

      {/* Global styles for animations */}
      <style>{`
        /* Widget animations */
        .widget-container-initial {
          animation: fadeInWithDelay 4s ease-out;
        }

        .widget-container-return {
          animation: fadeInImmediate 0.3s ease-out;
        }

        /* Close button hover animation */
        .close-button-wrapper {
          opacity: 0;
          transform: scale(0.8);
          transition: all 0.3s ease-out;
          transition-delay: 0.2s;
        }

        .close-button-wrapper.show {
          opacity: 1;
          transform: scale(1);
        }

        /* Chat interface zoom animation */
        .chat-interface-container {
          animation: zoomIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          transform-origin: bottom right;
        }

        .chat-interface-container.closing {
          animation: zoomOut 0.4s cubic-bezier(0.3, 0, 0.84, 0);
          transform-origin: bottom right;
        }

        @keyframes fadeInWithDelay {
          0% {
            opacity: 0;
            transform: scale(0);
          }
          74% {
            opacity: 0;
            transform: scale(0);
          }
          75% {
            opacity: 0;
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes fadeInImmediate {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.3);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes zoomOut {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(0.3);
          }
        }
      `}</style>
    </>
  );
};

export default Chatbot;
